import { type ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'

import { AppShellSkeleton } from '@/components/framework'
import { useAccountProfile } from '@/hooks/useAccountProfile'
import { useAuthCoordinator } from '@/hooks/useAuthCoordinator'
import { useAuthenticatedClient } from '@/hooks/useAuthenticatedClient'
import { ActionCableContext } from '@/lib/ActionCableContext'
import { createSigninRedirectArgs } from '@/lib/authRedirect'

export function AuthGuard({ children }: { children: ReactNode }) {
  const auth = useAuth()
  const { error, isAuthenticated, isLoading, signinRedirect, user } = auth
  const { t } = useTranslation()
  const { isSynced, consumer } = useAuthenticatedClient({
    user: user ?? null,
    isAuthenticated,
    isLoading,
  })
  const { isProfileReady, isProfileError, refetchProfile } = useAccountProfile({
    enabled: isSynced,
    userId: user?.profile.sub,
  })
  useAuthCoordinator()

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      signinRedirect(createSigninRedirectArgs())
    }
  }, [error, isAuthenticated, isLoading, signinRedirect])

  if (isLoading) {
    return <AppShellSkeleton />
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p role="alert" className="text-danger">
            {t('common.authenticationErrorPrefix')} {error.message}
          </p>
          <button
            type="button"
            className="mt-4 rounded bg-primary px-4 py-2 text-white hover:bg-primary-hover"
            onClick={() => signinRedirect(createSigninRedirectArgs())}
          >
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center">
        {t('common.redirectingToLogin')}
      </div>
    )
  }

  if (isAuthenticated && (!isSynced || !isProfileReady)) {
    if (isProfileError) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <p role="alert" className="text-danger">
              {t('common.profileLoadError')}
            </p>
            <button
              type="button"
              className="mt-4 rounded bg-primary px-4 py-2 text-white hover:bg-primary-hover"
              onClick={() => void refetchProfile()}
            >
              {t('common.tryAgain')}
            </button>
          </div>
        </div>
      )
    }
    return <AppShellSkeleton />
  }

  return <ActionCableContext.Provider value={consumer}>{children}</ActionCableContext.Provider>
}
